import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { DollarSign, Zap, FileText, TrendingUp, Building2 } from 'lucide-react';

// AI model pricing in SEK per 1M tokens (based on Azure/Google pricing * ~10.5 USD/SEK)
const MODEL_COSTS: Record<string, { input: number; output: number; label: string }> = {
  // Azure deployments
  'gpt-5-premium': { input: 52.50, output: 157.50, label: 'GPT-5 Premium' },
  'gpt-4.1-premium': { input: 52.50, output: 157.50, label: 'GPT-4.1 Premium' },
  'gpt-4o-mini': { input: 1.57, output: 6.30, label: 'GPT-4o Mini' },
  'gpt-5-mini': { input: 3.15, output: 12.60, label: 'GPT-5 Mini' },
  'gpt-4.1-mini': { input: 1.65, output: 6.60, label: 'GPT-4.1 Mini' },
  // Google Gemini
  'gemini-2.5-flash': { input: 0.79, output: 3.15, label: 'Gemini 2.5 Flash' },
  'gemini-2.5-pro': { input: 13.13, output: 52.50, label: 'Gemini 2.5 Pro' },
  // Default fallback
  'default': { input: 1.65, output: 6.60, label: 'Default (Mini)' }
};

interface CostSummary {
  totalCostSek: number;
  totalTokensIn: number;
  totalTokensOut: number;
  totalAnalyses: number;
  avgCostPerAnalysis: number;
}

interface CostByAudit {
  auditId: string;
  auditTitle: string;
  organizationName: string | null;
  moduleCount: number;
  tokensIn: number;
  tokensOut: number;
  costSek: number;
}

interface CostByModule {
  moduleType: string;
  analysisCount: number;
  tokensIn: number;
  tokensOut: number;
  totalCost: number;
  avgCost: number;
}

interface CostByOrganization {
  organizationId: string;
  organizationName: string;
  auditCount: number;
  analysisCount: number;
  tokensIn: number;
  tokensOut: number;
  totalCost: number;
}

interface DailyTrend {
  date: string;
  costSek: number;
  analyses: number;
}

const MODULE_LABELS: Record<string, string> = {
  'taxes_fees': 'Skatter & avgifter',
  'revenue': 'Intäkter',
  'accounts_payable': 'Leverantörsskulder',
  'accounts_receivable': 'Kundfordringar',
  'bank_cash': 'Bank & kassa',
  'journal_entries': 'Verifikationer',
  'vat': 'Moms',
  'data_quality': 'Datakvalitet',
  'duplicates_anomalies': 'Dubletter & anomalier'
};

export default function AdminAICostPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | 'all'>('7d');
  
  const [summary, setSummary] = useState<CostSummary>({
    totalCostSek: 0,
    totalTokensIn: 0,
    totalTokensOut: 0,
    totalAnalyses: 0,
    avgCostPerAnalysis: 0
  });
  const [costByAudit, setCostByAudit] = useState<CostByAudit[]>([]);
  const [costByModule, setCostByModule] = useState<CostByModule[]>([]);
  const [costByOrganization, setCostByOrganization] = useState<CostByOrganization[]>([]);
  const [dailyTrend, setDailyTrend] = useState<DailyTrend[]>([]);

  const getTimeFilter = () => {
    const now = new Date();
    switch (timeRange) {
      case '24h':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      default:
        return null;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const timeFilter = getTimeFilter();

    try {
      // Fetch revisions with audit info
      let revisionsQuery = supabase
        .from('revisions')
        .select(`
          id,
          module_type,
          total_tokens_in,
          total_tokens_out,
          total_cost_sek,
          audit_id,
          user_id,
          created_at,
          audits (
            id,
            title,
            user_id
          )
        `)
        .not('total_tokens_in', 'is', null);

      if (timeFilter) {
        revisionsQuery = revisionsQuery.gte('created_at', timeFilter);
      }

      const { data: revisions, error: revisionsError } = await revisionsQuery;
      
      if (revisionsError) {
        console.error('Error fetching revisions:', revisionsError);
        return;
      }

      // Fetch user roles to get organization info
      const userIds = [...new Set(revisions?.map(r => r.user_id) || [])];
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, organization_id, organizations (id, name)')
        .in('user_id', userIds);

      const userOrgMap = new Map<string, { id: string; name: string }>();
      userRoles?.forEach(ur => {
        if (ur.organizations) {
          userOrgMap.set(ur.user_id, { 
            id: (ur.organizations as any).id, 
            name: (ur.organizations as any).name 
          });
        }
      });

      // Calculate summary
      const totalCostSek = revisions?.reduce((sum, r) => sum + (r.total_cost_sek || 0), 0) || 0;
      const totalTokensIn = revisions?.reduce((sum, r) => sum + (r.total_tokens_in || 0), 0) || 0;
      const totalTokensOut = revisions?.reduce((sum, r) => sum + (r.total_tokens_out || 0), 0) || 0;
      const totalAnalyses = revisions?.length || 0;

      setSummary({
        totalCostSek,
        totalTokensIn,
        totalTokensOut,
        totalAnalyses,
        avgCostPerAnalysis: totalAnalyses > 0 ? totalCostSek / totalAnalyses : 0
      });

      // Group by audit
      const auditMap = new Map<string, CostByAudit>();
      revisions?.forEach(r => {
        if (!r.audit_id) return;
        const existing = auditMap.get(r.audit_id);
        const org = userOrgMap.get(r.user_id);
        if (existing) {
          existing.moduleCount++;
          existing.tokensIn += r.total_tokens_in || 0;
          existing.tokensOut += r.total_tokens_out || 0;
          existing.costSek += r.total_cost_sek || 0;
        } else {
          auditMap.set(r.audit_id, {
            auditId: r.audit_id,
            auditTitle: (r.audits as any)?.title || 'Okänt ärende',
            organizationName: org?.name || null,
            moduleCount: 1,
            tokensIn: r.total_tokens_in || 0,
            tokensOut: r.total_tokens_out || 0,
            costSek: r.total_cost_sek || 0
          });
        }
      });
      setCostByAudit(Array.from(auditMap.values()).sort((a, b) => b.costSek - a.costSek));

      // Group by module
      const moduleMap = new Map<string, CostByModule>();
      revisions?.forEach(r => {
        const moduleType = r.module_type || 'unknown';
        const existing = moduleMap.get(moduleType);
        if (existing) {
          existing.analysisCount++;
          existing.tokensIn += r.total_tokens_in || 0;
          existing.tokensOut += r.total_tokens_out || 0;
          existing.totalCost += r.total_cost_sek || 0;
        } else {
          moduleMap.set(moduleType, {
            moduleType,
            analysisCount: 1,
            tokensIn: r.total_tokens_in || 0,
            tokensOut: r.total_tokens_out || 0,
            totalCost: r.total_cost_sek || 0,
            avgCost: 0
          });
        }
      });
      const moduleStats = Array.from(moduleMap.values()).map(m => ({
        ...m,
        avgCost: m.analysisCount > 0 ? m.totalCost / m.analysisCount : 0
      })).sort((a, b) => b.totalCost - a.totalCost);
      setCostByModule(moduleStats);

      // Group by organization
      const orgMap = new Map<string, CostByOrganization>();
      const auditUserMap = new Map<string, string>();
      revisions?.forEach(r => {
        if (r.audit_id) {
          auditUserMap.set(r.audit_id, r.user_id);
        }
      });

      revisions?.forEach(r => {
        const org = userOrgMap.get(r.user_id);
        if (!org) return;
        
        const existing = orgMap.get(org.id);
        if (existing) {
          existing.analysisCount++;
          existing.tokensIn += r.total_tokens_in || 0;
          existing.tokensOut += r.total_tokens_out || 0;
          existing.totalCost += r.total_cost_sek || 0;
        } else {
          orgMap.set(org.id, {
            organizationId: org.id,
            organizationName: org.name,
            auditCount: 0, // Will be calculated separately
            analysisCount: 1,
            tokensIn: r.total_tokens_in || 0,
            tokensOut: r.total_tokens_out || 0,
            totalCost: r.total_cost_sek || 0
          });
        }
      });

      // Count unique audits per org
      const orgAuditCounts = new Map<string, Set<string>>();
      revisions?.forEach(r => {
        const org = userOrgMap.get(r.user_id);
        if (!org || !r.audit_id) return;
        if (!orgAuditCounts.has(org.id)) {
          orgAuditCounts.set(org.id, new Set());
        }
        orgAuditCounts.get(org.id)!.add(r.audit_id);
      });
      
      const orgStats = Array.from(orgMap.values()).map(o => ({
        ...o,
        auditCount: orgAuditCounts.get(o.organizationId)?.size || 0
      })).sort((a, b) => b.totalCost - a.totalCost);
      setCostByOrganization(orgStats);

      // Daily trend
      const dailyMap = new Map<string, { costSek: number; analyses: number }>();
      revisions?.forEach(r => {
        const date = r.created_at.split('T')[0];
        const existing = dailyMap.get(date);
        if (existing) {
          existing.costSek += r.total_cost_sek || 0;
          existing.analyses++;
        } else {
          dailyMap.set(date, {
            costSek: r.total_cost_sek || 0,
            analyses: 1
          });
        }
      });
      
      const trend = Array.from(dailyMap.entries())
        .map(([date, data]) => ({ date, ...data }))
        .sort((a, b) => a.date.localeCompare(b.date));
      setDailyTrend(trend);

    } catch (err) {
      console.error('Error fetching AI cost data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const formatCost = (cost: number) => {
    if (cost < 0.01) return '< 0.01 kr';
    return `${cost.toFixed(2)} kr`;
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
    if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}k`;
    return tokens.toString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">AI-kostnader</h1>
          <p className="text-muted-foreground">Sammanställning av tokenkostnader per ärende, modul och organisation</p>
        </div>
        <Select value={timeRange} onValueChange={(v) => setTimeRange(v as typeof timeRange)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Senaste 24h</SelectItem>
            <SelectItem value="7d">Senaste 7 dagar</SelectItem>
            <SelectItem value="30d">Senaste 30 dagar</SelectItem>
            <SelectItem value="all">All tid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total kostnad</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-2xl font-bold">{formatCost(summary.totalCostSek)}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Tokens (in/ut)</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <div className="text-2xl font-bold">
                {formatTokens(summary.totalTokensIn)} / {formatTokens(summary.totalTokensOut)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Antal analyser</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{summary.totalAnalyses}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Snitt per analys</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{formatCost(summary.avgCostPerAnalysis)}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Kostnadstrend</CardTitle>
          <CardDescription>Daglig kostnad över tid</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-64 w-full" />
          ) : dailyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(d) => new Date(d).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                  className="text-xs"
                />
                <YAxis 
                  tickFormatter={(v) => `${v.toFixed(2)} kr`}
                  className="text-xs"
                />
                <Tooltip 
                  formatter={(value: number) => [`${value.toFixed(4)} kr`, 'Kostnad']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('sv-SE')}
                />
                <Line 
                  type="monotone" 
                  dataKey="costSek" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              Ingen data för vald period
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Module */}
        <Card>
          <CardHeader>
            <CardTitle>Kostnad per modul</CardTitle>
            <CardDescription>Fördelning av AI-kostnader mellan moduler</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : costByModule.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={costByModule.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" tickFormatter={(v) => `${v.toFixed(2)} kr`} />
                    <YAxis 
                      type="category" 
                      dataKey="moduleType" 
                      tickFormatter={(m) => MODULE_LABELS[m] || m}
                      width={120}
                    />
                    <Tooltip formatter={(value: number) => [`${value.toFixed(4)} kr`, 'Total kostnad']} />
                    <Bar dataKey="totalCost" fill="hsl(var(--primary))" radius={4} />
                  </BarChart>
                </ResponsiveContainer>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Modul</TableHead>
                      <TableHead className="text-right">Körningar</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Snitt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costByModule.map(m => (
                      <TableRow key={m.moduleType}>
                        <TableCell className="font-medium">
                          {MODULE_LABELS[m.moduleType] || m.moduleType}
                        </TableCell>
                        <TableCell className="text-right">{m.analysisCount}</TableCell>
                        <TableCell className="text-right">{formatCost(m.totalCost)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatCost(m.avgCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">Ingen data</div>
            )}
          </CardContent>
        </Card>

        {/* Cost by Organization */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Kostnad per organisation
            </CardTitle>
            <CardDescription>Fördelning mellan kunder/organisationer</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : costByOrganization.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisation</TableHead>
                    <TableHead className="text-right">Ärenden</TableHead>
                    <TableHead className="text-right">Analyser</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Kostnad</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costByOrganization.map(o => (
                    <TableRow key={o.organizationId}>
                      <TableCell className="font-medium">{o.organizationName}</TableCell>
                      <TableCell className="text-right">{o.auditCount}</TableCell>
                      <TableCell className="text-right">{o.analysisCount}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatTokens(o.tokensIn + o.tokensOut)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCost(o.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                Ingen organisationsdata (kontrollera user_roles-tabellen)
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cost by Audit */}
      <Card>
        <CardHeader>
          <CardTitle>Kostnad per ärende</CardTitle>
          <CardDescription>Detaljerad fördelning av AI-kostnader per revisionsärende</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : costByAudit.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ärende</TableHead>
                  <TableHead>Organisation</TableHead>
                  <TableHead className="text-right">Moduler</TableHead>
                  <TableHead className="text-right">Tokens In</TableHead>
                  <TableHead className="text-right">Tokens Out</TableHead>
                  <TableHead className="text-right">Kostnad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costByAudit.slice(0, 20).map(a => (
                  <TableRow key={a.auditId}>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {a.auditTitle}
                    </TableCell>
                    <TableCell>
                      {a.organizationName ? (
                        <Badge variant="outline">{a.organizationName}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{a.moduleCount}</TableCell>
                    <TableCell className="text-right">{formatTokens(a.tokensIn)}</TableCell>
                    <TableCell className="text-right">{formatTokens(a.tokensOut)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCost(a.costSek)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-muted-foreground">Ingen data för vald period</div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Prisreferens</CardTitle>
          <CardDescription>Aktuella priser per modell (SEK per 1M tokens)</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modell</TableHead>
                <TableHead className="text-right">Input</TableHead>
                <TableHead className="text-right">Output</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(MODEL_COSTS).filter(([k]) => k !== 'default').map(([key, cost]) => (
                <TableRow key={key}>
                  <TableCell className="font-medium">{cost.label}</TableCell>
                  <TableCell className="text-right">{cost.input.toFixed(2)} kr</TableCell>
                  <TableCell className="text-right">{cost.output.toFixed(2)} kr</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
